import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Mail,
  FileText,
  BookOpen,
  User,
  Target,
  Briefcase,
  Calendar,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  GraduationCap,
  Video,
  Phone
} from 'lucide-react';
import adminApiService from '../../services/adminApiService';

const WorkPipelineModule = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [showWorkHistoryModal, setShowWorkHistoryModal] = useState(false);
  const [showRolePreferencesModal, setShowRolePreferencesModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [studentDocuments, setStudentDocuments] = useState([]);
  const [studentPersonalDetails, setStudentPersonalDetails] = useState({});
  const [studentWorkHistory, setStudentWorkHistory] = useState({});
  const [studentRolePreferences, setStudentRolePreferences] = useState({});
  const [studentInterviewDetails, setStudentInterviewDetails] = useState({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [filters, setFilters] = useState({
    pipelineStatus: 'all',
    paymentStatus: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadStudents();
    loadStats();
  }, [filters, pagination.page, pagination.limit]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      if (searchTerm) {
        params.searchTerm = searchTerm;
      }

      const response = await adminApiService.workPipeline.getStudents(params);
      setStudents(response.students);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading work students:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await adminApiService.workPipeline.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading work stats:', error);
    }
  };

  const loadStudentDetails = async (studentId) => {
    try {
      const response = await adminApiService.workPipeline.getStudent(studentId);
      setSelectedStudent(response);
    } catch (error) {
      console.error('Error loading work student details:', error);
    }
  };

  const loadStudentDocuments = async (studentId) => {
    try {
      setLoadingDocuments(true);
      const documents = await adminApiService.workPipeline.getStudentDocuments(studentId);
      setStudentDocuments(documents || []);
    } catch (error) {
      console.error('Error loading work student documents:', error);
      setStudentDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadStudentPersonalDetails = async (studentId) => {
    try {
      const details = await adminApiService.workPipeline.getStudentPersonalDetails(studentId);
      setStudentPersonalDetails(details);
    } catch (error) {
      console.error('Error loading work student personal details:', error);
      setStudentPersonalDetails({});
    }
  };

  const loadStudentWorkHistory = async (studentId) => {
    try {
      const history = await adminApiService.workPipeline.getStudentWorkHistory(studentId);
      setStudentWorkHistory(history);
    } catch (error) {
      console.error('Error loading work student work history:', error);
      setStudentWorkHistory({});
    }
  };

  const loadStudentRolePreferences = async (studentId) => {
    try {
      const preferences = await adminApiService.workPipeline.getStudentRolePreferences(studentId);
      setStudentRolePreferences(preferences);
    } catch (error) {
      console.error('Error loading work student role preferences:', error);
      setStudentRolePreferences({});
    }
  };

  const loadStudentInterviewDetails = async (studentId) => {
    try {
      const interview = await adminApiService.workPipeline.getStudentInterviewDetails(studentId);
      setStudentInterviewDetails(interview);
    } catch (error) {
      console.error('Error loading work student interview details:', error);
      setStudentInterviewDetails({});
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student._id));
    }
  };

  const handleOpenDocumentsModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentDocuments(studentId);
    setShowDocumentsModal(true);
  };

  const handleOpenPersonalModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentPersonalDetails(studentId);
    setShowPersonalModal(true);
  };

  const handleOpenWorkHistoryModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentWorkHistory(studentId);
    setShowWorkHistoryModal(true);
  };

  const handleOpenRolePreferencesModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentRolePreferences(studentId);
    setShowRolePreferencesModal(true);
  };

  const handleOpenInterviewModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentInterviewDetails(studentId);
    setShowInterviewModal(true);
  };

  const getPipelineStatusColor = (status) => {
    const colors = {
      'consultation_scheduled': 'bg-blue-100 text-blue-800',
      'consultation_completed': 'bg-green-100 text-green-800',
      'onboarding': 'bg-yellow-100 text-yellow-800',
      'payment_pending': 'bg-orange-100 text-orange-800',
      'documents_pending': 'bg-purple-100 text-purple-800',
      'documents_under_review': 'bg-indigo-100 text-indigo-800',
      'documents_approved': 'bg-green-100 text-green-800',
      'interview_preparation': 'bg-blue-100 text-blue-800',
      'interview_scheduled': 'bg-purple-100 text-purple-800',
      'interview_completed': 'bg-green-100 text-green-800',
      'offer_received': 'bg-emerald-100 text-emerald-800',
      'offer_accepted': 'bg-teal-100 text-teal-800',
      'completed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-800',
      'unpaid': 'bg-red-100 text-red-800',
      'refunded': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const exportToCSV = async () => {
    try {
      const blob = await adminApiService.workPipeline.exportStudents(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work_students_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const calculateProgress = (student) => {
    const sections = ['personalDetails', 'workHistory', 'rolePreferences', 'documents', 'interview'];
    const completed = sections.filter(section => student.sectionsCompleted?.[section]).length;
    return Math.round((completed / sections.length) * 100);
  };

  if (loading && students.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202356]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Work Pipeline Management</h2>
          <p className="text-gray-600">Manage work abroad candidates and applications</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            onClick={() => {
              loadStudents();
              loadStats();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Candidates</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.totalStudents}</p>
              </div>
              <Users className="text-blue-600" size={24} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid Candidates</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.paidStudents}</p>
              </div>
              <DollarSign className="text-green-600" size={24} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Interviews Scheduled</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.interviewScheduled}</p>
              </div>
              <Calendar className="text-purple-600" size={24} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Offers Received</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.offerReceived}</p>
              </div>
              <Briefcase className="text-emerald-600" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter size={16} />
              Filters
              {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
          {selectedStudents.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{selectedStudents.length} selected</span>
            </div>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Status</label>
              <select
                value={filters.pipelineStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, pipelineStatus: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
              >
                <option value="all">All Status</option>
                <option value="consultation_scheduled">Consultation Scheduled</option>
                <option value="consultation_completed">Consultation Completed</option>
                <option value="onboarding">Onboarding</option>
                <option value="payment_pending">Payment Pending</option>
                <option value="documents_pending">Documents Pending</option>
                <option value="documents_under_review">Documents Under Review</option>
                <option value="documents_approved">Documents Approved</option>
                <option value="interview_preparation">Interview Preparation</option>
                <option value="interview_scheduled">Interview Scheduled</option>
                <option value="interview_completed">Interview Completed</option>
                <option value="offer_received">Offer Received</option>
                <option value="offer_accepted">Offer Accepted</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                value={filters.paymentStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
              >
                <option value="all">All Payment Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedStudents.length === students.length && students.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pipeline Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.map((student) => (
                <tr key={student._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student._id)}
                      onChange={() => handleSelectStudent(student._id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {student.userId?.firstName} {student.userId?.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{student.userId?.email}</div>
                      <div className="text-sm text-gray-500">{student.personalDetails?.phoneNumber}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPipelineStatusColor(student.pipelineStatus)}`}>
                      {student.pipelineStatus?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(student.paymentStatus)}`}>
                      {student.paymentStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${calculateProgress(student)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{calculateProgress(student)}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {new Date(student.lastActivityAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenPersonalModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Personal Details"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <User size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenWorkHistoryModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Work History"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <Briefcase size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenRolePreferencesModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Role Preferences"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <Target size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenInterviewModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Interview Details"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <Video size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenDocumentsModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Documents"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        onClick={() => window.open(`mailto:${student.userId?.email}`, '_blank')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Send Email"
                      >
                        <Mail size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Items per page:</label>
                <select
                  value={pagination.limit}
                  onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202356]"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Modal - Placeholder for now */}
      {showDocumentsModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Documents - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowDocumentsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {loadingDocuments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202356] mx-auto mb-2"></div>
                  <p className="text-gray-500">Loading documents...</p>
                </div>
              ) : studentDocuments.length > 0 ? (
                <div className="space-y-4">
                  {studentDocuments.map((document) => (
                    <div key={document._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{document.documentName || document.documentType}</h4>
                          <p className="text-sm text-gray-500">{document.documentType}</p>
                          <p className="text-xs text-gray-400">
                            Uploaded: {new Date(document.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          document.status === 'approved' ? 'bg-green-100 text-green-800' :
                          document.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          document.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {document.status ? document.status.replace('_', ' ') : 'uploaded'}
                        </span>
                      </div>
                      
                      {document.fileUrl && (
                        <div className="flex items-center gap-2 mt-2">
                          <a
                            href={document.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View Document
                          </a>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm text-gray-500">
                            {document.fileSize ? `${(document.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                          </span>
                        </div>
                      )}
                      
                      {document.adminNotes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded">
                          <p className="text-sm text-gray-700">{document.adminNotes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No documents uploaded yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Personal Details Modal */}
      {showPersonalModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Personal Details - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowPersonalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentPersonalDetails && Object.keys(studentPersonalDetails).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Basic Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Date of Birth</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.dateOfBirth 
                          ? new Date(studentPersonalDetails.dateOfBirth).toLocaleDateString() 
                          : 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Gender</label>
                      <p className="text-gray-900 capitalize">
                        {studentPersonalDetails.gender || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Marital Status</label>
                      <p className="text-gray-900 capitalize">
                        {studentPersonalDetails.maritalStatus || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Nationality</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.nationality || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Contact Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Country of Residence</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.countryOfResidence || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">City</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.city || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Address</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.address || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Postal Code</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.postalCode || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Phone Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Phone Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Primary Phone</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.phoneNumber || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Alternate Phone</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.alternatePhone || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Work-Specific Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Work Preferences</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Relocation Readiness</label>
                      <p className="text-gray-900 capitalize">
                        {studentPersonalDetails.relocationReadiness?.replace('_', ' ') || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Available to Start</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.availableToStart 
                          ? new Date(studentPersonalDetails.availableToStart).toLocaleDateString() 
                          : 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-4 md:col-span-2">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Emergency Contact</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">Contact Name</label>
                        <p className="text-gray-900">
                          {studentPersonalDetails.emergencyContactName || 'Not provided'}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm text-gray-500">Contact Phone</label>
                        <p className="text-gray-900">
                          {studentPersonalDetails.emergencyContactPhone || 'Not provided'}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm text-gray-500">Relationship</label>
                        <p className="text-gray-900">
                          {studentPersonalDetails.emergencyContactRelationship || 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <User className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No personal details provided yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Work History Modal */}
      {showWorkHistoryModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Work History - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowWorkHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentWorkHistory && Object.keys(studentWorkHistory).length > 0 ? (
                <div className="space-y-6">
                  {/* Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Experience Overview</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Total Years of Experience</label>
                          <p className="text-gray-900 font-medium">
                            {studentWorkHistory.totalYearsExperience || 'Not provided'} years
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Current Employment Status</label>
                          <p className="text-gray-900 capitalize">
                            {studentWorkHistory.currentEmploymentStatus?.replace('_', ' ') || 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Quick Stats</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Total Jobs</label>
                          <p className="text-gray-900 font-medium">
                            {studentWorkHistory.jobs?.length || 0} positions
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Total Skills</label>
                          <p className="text-gray-900 font-medium">
                            {studentWorkHistory.skills?.length || 0} skills
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Total Certifications</label>
                          <p className="text-gray-900 font-medium">
                            {studentWorkHistory.certifications?.length || 0} certifications
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Jobs */}
                  {studentWorkHistory.jobs && studentWorkHistory.jobs.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Employment History</h4>
                      <div className="space-y-4">
                        {studentWorkHistory.jobs.map((job, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm text-gray-500">Job Title</label>
                                <p className="text-gray-900 font-medium">{job.jobTitle || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Employer</label>
                                <p className="text-gray-900">{job.employer || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Location</label>
                                <p className="text-gray-900">
                                  {job.city ? `${job.city}, ${job.country}` : job.country || 'Not provided'}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Duration</label>
                                <p className="text-gray-900">
                                  {job.startDate ? new Date(job.startDate).toLocaleDateString() : 'Not provided'} - 
                                  {job.isCurrentJob ? 'Present' : 
                                   job.endDate ? new Date(job.endDate).toLocaleDateString() : 'Not provided'}
                                </p>
                              </div>
                            </div>
                            
                            {job.responsibilities && (
                              <div className="mt-4">
                                <label className="text-sm text-gray-500">Responsibilities</label>
                                <p className="text-gray-900 mt-1">{job.responsibilities}</p>
                              </div>
                            )}
                            
                            {job.achievements && (
                              <div className="mt-4">
                                <label className="text-sm text-gray-500">Key Achievements</label>
                                <p className="text-gray-900 mt-1">{job.achievements}</p>
                              </div>
                            )}
                            
                            {job.reasonForLeaving && !job.isCurrentJob && (
                              <div className="mt-4">
                                <label className="text-sm text-gray-500">Reason for Leaving</label>
                                <p className="text-gray-900 mt-1">{job.reasonForLeaving}</p>
                              </div>
                            )}
                            
                            {job.isCurrentJob && (
                              <div className="mt-3">
                                <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  Current Position
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  {studentWorkHistory.skills && studentWorkHistory.skills.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {studentWorkHistory.skills.map((skill, index) => (
                          <span key={index} className="inline-flex px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  {studentWorkHistory.certifications && studentWorkHistory.certifications.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Certifications</h4>
                      <div className="space-y-3">
                        {studentWorkHistory.certifications.map((cert, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div>
                                <label className="text-sm text-gray-500">Certification Name</label>
                                <p className="text-gray-900 font-medium">{cert.name || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Issuing Organization</label>
                                <p className="text-gray-900">{cert.issuingOrganization || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Issue Date</label>
                                <p className="text-gray-900">
                                  {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : 'Not provided'}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Expiry Date</label>
                                <p className="text-gray-900">
                                  {cert.expiryDate ? new Date(cert.expiryDate).toLocaleDateString() : 'No expiry'}
                                </p>
                              </div>
                            </div>
                            {cert.credentialId && (
                              <div className="mt-3">
                                <label className="text-sm text-gray-500">Credential ID</label>
                                <p className="text-gray-900">{cert.credentialId}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {studentWorkHistory.languages && studentWorkHistory.languages.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Languages</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {studentWorkHistory.languages.map((lang, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900">{lang.language}</span>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                lang.proficiency === 'native' ? 'bg-green-100 text-green-800' :
                                lang.proficiency === 'advanced' ? 'bg-blue-100 text-blue-800' :
                                lang.proficiency === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {lang.proficiency}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No work history provided yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Role Preferences Modal */}
      {showRolePreferencesModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Role Preferences - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowRolePreferencesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentRolePreferences && Object.keys(studentRolePreferences).length > 0 ? (
                <div className="space-y-6">
                  {/* Selected Roles */}
                  {studentRolePreferences.selectedRoles && studentRolePreferences.selectedRoles.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Selected Roles</h4>
                      <div className="space-y-3">
                        {studentRolePreferences.selectedRoles.map((selection, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="font-medium text-gray-900">
                                  {selection.roleId?.title || 'Role Title Not Available'}
                                </h5>
                                <p className="text-sm text-gray-600">
                                  {selection.roleId?.company || 'Company Not Available'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {selection.roleId?.location || 'Location Not Available'}
                                </p>
                              </div>
                              <div className="text-right">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  selection.priority === 1 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  Priority {selection.priority}
                                </span>
                              </div>
                            </div>
                            {selection.roleId?.description && (
                              <div className="mt-3">
                                <label className="text-sm text-gray-500">Role Description</label>
                                <p className="text-gray-900 mt-1 text-sm">{selection.roleId.description}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work Preferences */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Work Preferences</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Availability Timeline</label>
                          <p className="text-gray-900 capitalize">
                            {studentRolePreferences.availabilityTimeline?.replace('_', ' ') || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Minimum Salary Expectation</label>
                          <p className="text-gray-900">
                            {studentRolePreferences.minimumSalaryExpectation || 'Not specified'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Flexibility</h4>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            studentRolePreferences.willingToWorkShifts 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {studentRolePreferences.willingToWorkShifts ? 'Willing to Work Shifts' : 'Not Willing to Work Shifts'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            studentRolePreferences.willingToRelocateWithinCountry 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {studentRolePreferences.willingToRelocateWithinCountry ? 'Willing to Relocate Within Country' : 'Not Willing to Relocate'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Preferences */}
                  {studentRolePreferences.additionalPreferences && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Additional Preferences</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-900">
                          {studentRolePreferences.additionalPreferences}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No role preferences provided yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Interview Details Modal */}
      {showInterviewModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Interview Details - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowInterviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentInterviewDetails && Object.keys(studentInterviewDetails).length > 0 ? (
                <div className="space-y-6">
                  {/* Interview Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Interview Information</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Employer</label>
                          <p className="text-gray-900 font-medium">
                            {studentInterviewDetails.employer || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Role</label>
                          <p className="text-gray-900">
                            {studentInterviewDetails.role || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Interview Format</label>
                          <p className="text-gray-900 capitalize">
                            {studentInterviewDetails.interviewFormat?.replace('_', ' ') || 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Schedule</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Interview Date</label>
                          <p className="text-gray-900">
                            {studentInterviewDetails.interviewDate 
                              ? new Date(studentInterviewDetails.interviewDate).toLocaleDateString() 
                              : 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Interview Time</label>
                          <p className="text-gray-900">
                            {studentInterviewDetails.interviewTime || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Status</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            studentInterviewDetails.interviewStatus === 'completed' ? 'bg-green-100 text-green-800' :
                            studentInterviewDetails.interviewStatus === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                            studentInterviewDetails.interviewStatus === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {studentInterviewDetails.interviewStatus?.replace('_', ' ') || 'Not set'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Interview Notes */}
                  {studentInterviewDetails.interviewNotes && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Interview Notes</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-900">
                          {studentInterviewDetails.interviewNotes}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Interview Feedback */}
                  {studentInterviewDetails.feedback && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Interview Feedback</h4>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-gray-900">
                          {studentInterviewDetails.feedback}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {studentInterviewDetails.adminNotes && studentInterviewDetails.adminNotes.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Admin Notes</h4>
                      <div className="space-y-2">
                        {studentInterviewDetails.adminNotes.map((note, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {note.addedBy?.firstName} {note.addedBy?.lastName}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(note.addedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{note.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interview Actions */}
                  <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                    <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      <Video size={16} />
                      Join Interview
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Phone size={16} />
                      Call Candidate
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Mail size={16} />
                      Send Email
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Video className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No interview details available yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default WorkPipelineModule;
